import React from 'react';

import { Center, Loader, Stack, Text } from '@mantine/core';
import type { AxiosError } from 'axios';
import AlreadyPurchasedNotice from 'components/SalesPortal/AlreadyPurchasedNotice';
import { useAuth } from 'context/auth-context';
import useSalesPortalBillingPlan from 'hooks/salesPortal/useSalesPortalBillingPlan';
import useSalesPortalConfiguration from 'hooks/salesPortal/useSalesPortalConfiguration';
import useSalesPortalPackage from 'hooks/salesPortal/useSalesPortalPackage';
import Header from 'layouts/SalesPortalLayout/components/Header';
import NotFound from 'pages/shared/NotFound';
import { useParams } from 'react-router-dom';

import Checkout from './components/Checkout/Checkout';
import BillingPlanError from './components/Checkout/components/BillingPlanError';
import useAutoAthleteProfileSwitchEffect from './useSwitchToAthleteProfileEffect';

const SalesPortalBillingPlanPage = (): JSX.Element => {
  const { session } = useAuth();
  const { salesPortalSlug, packageId, billingPlanId } = useParams<{
    salesPortalSlug: string;
    packageId: string;
    billingPlanId: string;
  }>();

  const {
    data: salesPortalConfig,
    isError: salesPortalConfigError,
    isLoading: salesPortalConfigLoading,
  } = useSalesPortalConfiguration(salesPortalSlug);
  const salesPortalIsDisabled = salesPortalConfig && !salesPortalConfig.data.enabled;

  const {
    data: packageData,
    isError: packageError,
    isLoading: packageLoading,
  } = useSalesPortalPackage({
    gym: salesPortalConfig?.data.gym.gymId,
    packageId,
    profileId: session?.profile?.profileId,
  });
  const packageIsDisabled = packageData && packageData.data.salesPortalStatus === 'DISABLED';

  const {
    data: billingPlanData,
    isError: billingPlanError,
    error: billingPlanFetchError,
    isLoading: billingPlanLoading,
  } = useSalesPortalBillingPlan({ gym: salesPortalConfig?.data.gym.gymId, packageId, billingPlanId });
  const billingPlanIsDisabled = billingPlanData && billingPlanData.data.salesPortalStatus === 'DISABLED';
  // The retrieve endpoint answers 400 for a plan the gym has taken out of the portal, so an
  // unavailable plan never arrives as data to read `salesPortalStatus` from. `keepPreviousData`
  // means a plan disabled mid-checkout also lands here, holding stale data behind a failed refetch.
  const billingPlanIsUnavailable =
    billingPlanError && (billingPlanFetchError as AxiosError | undefined)?.response?.status === 400;

  const isDemoGym = salesPortalConfig?.data.gym.isDemoGym || false;
  const isPackageSoldOut =
    packageData?.data &&
    packageData.data?.maxSalesAllowed !== null &&
    Number(packageData.data?.numValidSales) >= packageData.data?.maxSalesAllowed;

  const isAlreadyPurchased = !!session?.user && Boolean(packageData?.data?.alreadyPurchased);

  const isCheckoutDisabled = isDemoGym || isPackageSoldOut || isAlreadyPurchased;

  useAutoAthleteProfileSwitchEffect(salesPortalConfig?.data.gym.gymId);

  // Only block on the first load of each resource — plan changes inside the
  // checkout refetch the billing plan in place (keepPreviousData) and must not
  // unmount the accordion.
  if (
    (salesPortalConfigLoading && !salesPortalConfig) ||
    (packageLoading && !packageData) ||
    (billingPlanLoading && !billingPlanData)
  ) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Loader />
      </Center>
    );
  }

  if (salesPortalConfigError || packageError || salesPortalIsDisabled || packageIsDisabled) {
    return <NotFound />;
  }

  // A disabled billing plan gets the dedicated error screen, not a 404 — fires before the
  // accordion renders, and re-fires consistently on refresh. A plan disabled while a buyer
  // is mid-checkout lands here too once the plan query refetches.
  if (billingPlanIsDisabled || billingPlanIsUnavailable) {
    return (
      <>
        <Header logo={salesPortalConfig?.data.logo} gymTitle={salesPortalConfig?.data.gym.title} />
        <Stack className='content'>
          <BillingPlanError />
        </Stack>
      </>
    );
  }

  // Anything else that broke the plan fetch (transport, 5xx) is not a "this offer is
  // unavailable" story for the buyer.
  if (billingPlanError) {
    return <NotFound />;
  }

  return (
    <>
      <Header logo={salesPortalConfig?.data.logo} gymTitle={salesPortalConfig?.data.gym.title} />
      <Stack className='content'>
        {isAlreadyPurchased && <AlreadyPurchasedNotice browseTo={`/sales-portal/${salesPortalSlug}`} />}
        {isCheckoutDisabled && !isAlreadyPurchased && (
          <Center>
            <Text>Checkout is Disabled</Text>
          </Center>
        )}
        {!isCheckoutDisabled && packageData?.data && billingPlanData?.data && (
          <Checkout packageData={packageData.data} billingPlan={billingPlanData.data} />
        )}
      </Stack>
    </>
  );
};

export default SalesPortalBillingPlanPage;
